"use client";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { safeLocalStorage } from "@/lib/safeStorage";

const STORAGE_KEY = "cookie-consent";

type CookieConsentContextType = {
  /** null = undecided (banner shows), true = accepted, false = declined */
  hasConsent: boolean | null;
  isInitialized: boolean;
  updateConsent: (consent: boolean) => void;
  /** Withdraw a previous choice: clears storage and reopens the banner. */
  resetConsent: () => void;
};

const CookieConsentContext = createContext<
  CookieConsentContextType | undefined
>(undefined);

export const CookieConsentProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [hasConsent, setHasConsent] = useState<boolean | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const consentValue = safeLocalStorage.getItem(STORAGE_KEY);
    setHasConsent(consentValue !== null ? consentValue === "true" : null);
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // sessionStorage writes also emit "storage"; ignore them.
      if (e.storageArea && e.storageArea !== window.localStorage) return;
      if (e.key !== STORAGE_KEY) return;

      // newValue is null when another tab removed the key (a withdrawal).
      // Coercing that to false would record "declined" and swallow the reset.
      setHasConsent(e.newValue === null ? null : e.newValue === "true");
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const updateConsent = useCallback((consent: boolean) => {
    safeLocalStorage.setItem(STORAGE_KEY, String(consent));
    setHasConsent(consent);
  }, []);

  const resetConsent = useCallback(() => {
    safeLocalStorage.removeItem(STORAGE_KEY);
    setHasConsent(null);
  }, []);

  const value = useMemo(
    () => ({ hasConsent, isInitialized, updateConsent, resetConsent }),
    [hasConsent, isInitialized, updateConsent, resetConsent]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = (): CookieConsentContextType => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error(
      "useCookieConsent must be used within a CookieConsentProvider"
    );
  }
  return context;
};
